import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

// All wallet values in backend are USD. We convert at display time.
// `symbol` = Arabic abbreviation; `symbolEn` = English/Latin abbreviation.
export const CURRENCIES = {
  USD: { code: "USD", symbol: "$",     symbolEn: "$",   rate: 1,      locale: "en-US" },
  SAR: { code: "SAR", symbol: "ر.س",   symbolEn: "SAR", rate: 3.75,   locale: "ar-SA" },
  AED: { code: "AED", symbol: "د.إ",   symbolEn: "AED", rate: 3.6725, locale: "ar-AE" },
  EGP: { code: "EGP", symbol: "ج.م",   symbolEn: "EGP", rate: 47.5,   locale: "ar-EG" },
};

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const { i18n } = useTranslation();
  const [currency, setCurrencyState] = useState(() => {
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem("lm_currency") : null;
    return saved && CURRENCIES[saved] ? saved : "USD";
  });

  useEffect(() => {
    if (typeof localStorage !== "undefined") localStorage.setItem("lm_currency", currency);
  }, [currency]);

  const setCurrency = useCallback((c) => {
    if (CURRENCIES[c]) setCurrencyState(c);
  }, []);

  const convert = useCallback((usd) => {
    if (usd == null || !Number.isFinite(Number(usd))) return 0;
    return Number(usd) * CURRENCIES[currency].rate;
  }, [currency]);

  // Pick the right symbol based on UI language (English = Latin abbreviation,
  // Arabic = native abbreviation)
  const activeSymbol = useCallback(() => {
    const meta = CURRENCIES[currency];
    return i18n.language?.startsWith("ar") ? meta.symbol : meta.symbolEn;
  }, [currency, i18n.language]);

  // Single-decimal formatter (max 1 fraction digit by default)
  const fmt = useCallback((usd, opts = {}) => {
    const value = convert(usd);
    const max = opts.maxDecimals ?? 1;
    const min = opts.minDecimals ?? 0;
    const num = value.toLocaleString("en-US", {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    });
    const sym = activeSymbol();
    // USD always prefixes with "$"; other currencies suffix the abbreviation
    return currency === "USD" ? `${sym}${num}` : `${num} ${sym}`;
  }, [currency, convert, activeSymbol]);

  const meta = {
    ...CURRENCIES[currency],
    // Override `symbol` on `meta` so existing call sites that read meta.symbol
    // get the language-appropriate abbreviation automatically.
    symbol: activeSymbol(),
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convert, fmt, meta }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
